import React from 'react';
import Button from '../../../components/ui/Button';

const ApiKeyList = ({ 
  apiKeys, 
  revealedKeys, 
  onRevealKey, 
  onToggleKey, 
  onDeleteKey, 
  onViewDetails, 
  onCopyToClipboard 
}) => {
  const maskApiKey = (key) => {
    if (!key) return '';
    const parts = key?.split('_');
    if (parts?.length >= 3) {
      return `${parts?.[0]}_${parts?.[1]}_${'•'?.repeat(16)}`;
    }
    return '•'?.repeat(key?.length - 8) + key?.slice(-8);
  };

  const getKeyTypeColor = (type) => {
    switch (type) {
      case 'production':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'development':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'webhook':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'inactive':
        return 'bg-yellow-500';
      case 'revoked':
        return 'bg-red-500';
      default:
        return 'bg-gray-400';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatLastUsed = (dateString) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math?.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Less than an hour ago';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInHours < 48) return 'Yesterday';
    
    const diffInDays = Math?.floor(diffInHours / 24);
    if (diffInDays < 30) return `${diffInDays} days ago`;
    
    return date?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="divide-y divide-gray-200">
      {apiKeys?.map((apiKey) => (
        <div key={apiKey?.id} className="p-6 hover:bg-gray-50 transition-colors">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {/* Header with name, type, and status */}
              <div className="flex items-center space-x-3 mb-3">
                <h3 className="text-lg font-semibold text-gray-900">{apiKey?.name}</h3>
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getKeyTypeColor(apiKey?.type)}`}>
                  {apiKey?.type?.charAt(0)?.toUpperCase() + apiKey?.type?.slice(1)}
                </span>
                <div className={`w-2 h-2 rounded-full ${getStatusColor(apiKey?.status)}`} title={apiKey?.status} />
                <span className="text-xs text-gray-500 capitalize">{apiKey?.status}</span>
              </div>

              {/* API Key Display */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <code className="text-sm font-mono text-gray-900 truncate">
                      {revealedKeys?.has(apiKey?.id) ? apiKey?.key_hash : maskApiKey(apiKey?.key_hash)}
                    </code>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRevealKey?.(apiKey?.id)}
                        className="flex items-center space-x-1"
                        title={revealedKeys?.has(apiKey?.id) ? 'Hide key' : 'Show key'}
                      >
                        <span>{revealedKeys?.has(apiKey?.id) ? '🙈' : '👁'}</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onCopyToClipboard?.(apiKey?.key_hash)}
                        className="flex items-center space-x-1"
                        title="Copy to clipboard"
                      >
                        <span>📋</span>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Key Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Created</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(apiKey?.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Last Used</p>
                  <p className="text-sm font-medium text-gray-900">{formatLastUsed(apiKey?.last_used_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Rate Limit</p>
                  <p className="text-sm font-medium text-gray-900">{apiKey?.rate_limit?.toLocaleString()} req/hour</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Permissions</p>
                  <p className="text-sm font-medium text-gray-900">{apiKey?.permissions?.length || 0} permissions</p>
                </div>
              </div>

              {/* Permissions List */}
              {apiKey?.permissions?.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-gray-600 mb-2">Permissions</p>
                  <div className="flex flex-wrap gap-2">
                    {apiKey?.permissions?.slice(0, 5)?.map((permission) => (
                      <span key={permission} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full border border-blue-200">
                        {permission}
                      </span>
                    ))}
                    {apiKey?.permissions?.length > 5 && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full border border-gray-200">
                        +{apiKey?.permissions?.length - 5} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Expiration Warning */}
              {apiKey?.expires_at && new Date(apiKey?.expires_at) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <span className="text-yellow-600 text-sm mt-0.5">⚠️</span>
                    <div>
                      <p className="text-sm text-yellow-800 font-medium">Expiring Soon</p>
                      <p className="text-xs text-yellow-700">
                        This API key will expire on {formatDate(apiKey?.expires_at)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewDetails?.(apiKey)}
                className="text-blue-600 hover:bg-blue-50"
              >
                Details
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onToggleKey?.(apiKey?.id, apiKey?.status)}
                className={apiKey?.status === 'active' ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'}
                disabled={apiKey?.status === 'revoked'}
              >
                {apiKey?.status === 'active' ? 'Deactivate' : 'Activate'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDeleteKey?.(apiKey?.id)}
                className="text-red-600 hover:bg-red-50 flex items-center"
              >
                <span>🗑</span>
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ApiKeyList;